/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * RGBARRY-CUSTOMIZATION: Klaviyo welcome-verification landing page.
 *
 * Headless replacement for the SFRA `Welcome-Validate` controller. The shopper arrives
 * here from the Klaviyo verification email link (`/welcome/validate?token=...&email=...`).
 * The loader calls the custom SCAPI `/verify` endpoint (guest token) which compares the
 * one-time `registrationToken` and re-enables the account on a match. On a token mismatch
 * we mirror SFRA and send the shopper to password reset.
 *
 * This storefront runs with `url.prefix: '/'` (no `/:siteId/:localeId` segment — see
 * config.server.ts), so every route is already served at the root and the instance-side
 * Klaviyo email link stays locale-agnostic (`/welcome/validate?...`) without any special
 * routing. The route is defined at the top level (not nested under the pathless `_empty`
 * layout), so it reproduces that layout's chrome-less shell (SkipLink + <main>) here.
 * Site context (used by the SCAPI verify call and the redirect prefixing below) is
 * resolved from cookies/config defaults by the site-context middleware.
 */
import type { ReactElement } from 'react';
import { redirect } from 'react-router';
import type { Route } from './+types/welcome.validate';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { SkipLink } from '@/components/skip-link';
import { Card } from '@/components/ui/card';
import { SeoMeta } from '@/components/seo-meta';
import { routes } from '@/route-paths';
import { buildUrlFromContext } from '@/lib/url.server';
import { verifyWelcomeToken } from '@/lib/marketing/klaviyo.server';
import { getLogger } from '@/lib/logger.server';

type WelcomeValidateData = {
    status: 'verified' | 'error';
};

export async function loader({ request, context }: Route.LoaderArgs): Promise<WelcomeValidateData | Response> {
    const logger = getLogger(context);
    const url = new URL(request.url);
    const token = url.searchParams.get('token');
    const email = url.searchParams.get('email');

    if (!token || !email) {
        logger.warn('WelcomeValidate: missing token or email; redirecting to login');
        return redirect(buildUrlFromContext(routes.login, context));
    }

    try {
        const { verified } = await verifyWelcomeToken(context, { email, token });

        if (!verified) {
            // Token mismatch / unknown login — mirror SFRA and route to password reset,
            // which also re-enables the account once a new password is set.
            logger.info('WelcomeValidate: token not accepted; redirecting to password reset');
            return redirect(buildUrlFromContext(routes.resetPassword, context));
        }

        logger.info('WelcomeValidate: account verified');
        return { status: 'verified' };
    } catch (error) {
        logger.error('WelcomeValidate: verification call failed', { error });
        return { status: 'error' };
    }
}

export default function WelcomeValidate({ loaderData }: { loaderData: WelcomeValidateData }): ReactElement {
    const { t } = useTranslation('welcome');
    const isVerified = loaderData.status === 'verified';

    return (
        <>
            <SkipLink />
            <main
                id="main-content"
                tabIndex={-1}
                data-layout="empty"
                data-section="auth"
                className="min-h-screen flex items-center justify-center bg-background py-12 section-container">
                <SeoMeta
                    title={t('meta.title', { defaultValue: 'Account Verification' })}
                    description={t('meta.description', { defaultValue: 'Verify your account.' })}
                    noIndex
                />
                <div className="max-w-md w-full space-y-8 text-center">
                    <h1 className="text-3xl font-bold text-foreground">
                        {isVerified ? t('verified.title') : t('error.title')}
                    </h1>
                    <Card className="p-8 space-y-6">
                        <p className="text-sm text-muted-foreground">
                            {isVerified ? t('verified.message') : t('error.message')}
                        </p>
                        <Link
                            to={routes.login}
                            className="inline-flex items-center justify-center font-medium text-primary hover:underline">
                            {isVerified ? t('verified.cta') : t('error.cta')}
                        </Link>
                    </Card>
                </div>
            </main>
        </>
    );
}
